import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { appConfig } from '../config/featureFlags';
import { PATHS } from '../constants/Paths.constants';
import { useAppSelector } from '../hooks/redux';
import Modal from './ui/Modal/Modal';
import { PrimaryButton, WhiteButton } from './ui/Button/Buttons';

const IDLE_DEADLINE_STORAGE_KEY = 'euaiaudit_idle_deadline_at';
const IDLE_LOGOUT_STORAGE_KEY = 'euaiaudit_idle_logout_at';
const ACTIVITY_THROTTLE_MS = 1500; // Throttle activity events to avoid excessive writes to localStorage
const COUNTDOWN_INTERVAL_MS = 1000; // Update countdown every second
const ACTIVITY_EVENTS = [
  'click',
  'keydown',
  'mousedown',
  'mousemove',
  'scroll',
  'touchstart'
] as const;

export function IdleSessionTimeout() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const [remainingMs, setRemainingMs] = useState(appConfig.idleTimeoutMs);
  const remainingMsRef = useRef(appConfig.idleTimeoutMs);
  const logoutStartedRef = useRef(false);
  const activityThrottleRef = useRef<number | null>(null);

  const idleTimeoutMs = appConfig.idleTimeoutMs;
  const warningDurationMs = useMemo(
    () => Math.min(appConfig.idleWarningMs, idleTimeoutMs),
    [idleTimeoutMs]
  );
  const isEnabled = idleTimeoutMs > 0;

  const updateRemainingMs = useCallback((nextRemainingMs: number) => {
    remainingMsRef.current = nextRemainingMs;
    setRemainingMs(nextRemainingMs);
  }, []);

  const resetDeadline = useCallback(() => {
    if (!isEnabled || !isAuthenticated || logoutStartedRef.current) {
      return;
    }

    const nextDeadline = Date.now() + idleTimeoutMs;
    localStorage.setItem(IDLE_DEADLINE_STORAGE_KEY, String(nextDeadline));
    updateRemainingMs(idleTimeoutMs);
  }, [idleTimeoutMs, isAuthenticated, isEnabled, updateRemainingMs]);

  const startLogout = useCallback(() => {
    if (logoutStartedRef.current) {
      return;
    }

    logoutStartedRef.current = true;
    localStorage.removeItem(IDLE_DEADLINE_STORAGE_KEY);
    localStorage.setItem(IDLE_LOGOUT_STORAGE_KEY, String(Date.now()));
    navigate(PATHS.LOGOUT, { replace: true });
  }, [navigate]);

  const handleActivity = useCallback(() => {
    if (activityThrottleRef.current) {
      return;
    }

    if (remainingMsRef.current <= warningDurationMs) {
      return;
    }

    resetDeadline();
    activityThrottleRef.current = window.setTimeout(() => {
      activityThrottleRef.current = null;
    }, ACTIVITY_THROTTLE_MS);
  }, [resetDeadline, warningDurationMs]);

  useEffect(() => {
    if (!isEnabled || !isAuthenticated || location.pathname === PATHS.LOGOUT) {
      localStorage.removeItem(IDLE_DEADLINE_STORAGE_KEY);
      logoutStartedRef.current = false;
      return;
    }

    logoutStartedRef.current = false;
    resetDeadline();

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleActivity();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const countdownIntervalId = window.setInterval(() => {
      const deadline = Number(localStorage.getItem(IDLE_DEADLINE_STORAGE_KEY));
      if (!Number.isFinite(deadline)) {
        resetDeadline();
        return;
      }

      const nextRemainingMs = Math.max(deadline - Date.now(), 0);
      const wasInWarningPeriod = remainingMsRef.current <= warningDurationMs;
      const isInWarningPeriod = nextRemainingMs <= warningDurationMs;

      if (isInWarningPeriod || wasInWarningPeriod) {
        updateRemainingMs(nextRemainingMs);
      }

      if (nextRemainingMs <= 0) {
        startLogout();
      }
    }, COUNTDOWN_INTERVAL_MS);

    const handleStorage = (event: StorageEvent) => {
      if (event.key === IDLE_LOGOUT_STORAGE_KEY && event.newValue) {
        startLogout();
        return;
      }

      if (event.key !== IDLE_DEADLINE_STORAGE_KEY || !event.newValue) {
        return;
      }

      const nextDeadline = Number(event.newValue);
      if (Number.isFinite(nextDeadline)) {
        updateRemainingMs(Math.max(nextDeadline - Date.now(), 0));
      }
    };

    window.addEventListener('storage', handleStorage);

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorage);
      window.clearInterval(countdownIntervalId);

      if (activityThrottleRef.current) {
        window.clearTimeout(activityThrottleRef.current);
        activityThrottleRef.current = null;
      }
    };
  }, [
    handleActivity,
    isAuthenticated,
    isEnabled,
    location.pathname,
    resetDeadline,
    startLogout,
    updateRemainingMs,
    warningDurationMs
  ]);

  const remainingSeconds = Math.ceil(remainingMs / 1000);
  const shouldShowWarning =
    isEnabled &&
    isAuthenticated &&
    !logoutStartedRef.current &&
    remainingMs > 0 &&
    remainingMs <= warningDurationMs;

  return (
    <Modal
      open={shouldShowWarning}
      dialogPanelClassnames="sm:w-full sm:max-w-md"
    >
      <div>
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Your session is about to expire
        </h3>
        <p className="mt-3 text-sm text-gray-600">
          You will be signed out in {remainingSeconds} seconds due to
          inactivity.
        </p>
        <div className="mt-6 flex items-center justify-end gap-3">
          <WhiteButton type="button" onClick={resetDeadline}>
            Stay signed in
          </WhiteButton>
          <PrimaryButton type="button" onClick={startLogout}>
            Sign out
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
}
